/**
 * AdminFounderMode — control panel for Founder Operating Mode.
 *
 * Two clearly separated entry points:
 *   REAL ACCESS (production data, no Stripe charges, no test pollution)
 *   DEMO ACCESS (seeded test data, founder-tagged, safe to reset)
 */
import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Crown, RotateCcw, Play, ShieldAlert, ShieldCheck, ScrollText } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";
import { clearRoleScopedStorage } from "../utils/roleStorage";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { id: "adi",     label: "ADI",     helper: "Qualified instructor onboarding wizard." },
  { id: "pdi",     label: "PDI",     helper: "Trainee instructor onboarding (Part 2/3)." },
  { id: "learner", label: "Learner", helper: "Brand-new learner experience on /learn." },
];

const MODES = [
  {
    kind: "real",
    title: "REAL ACCESS",
    subtitle: "Production data only. Test/demo records are filtered out. Stripe is bypassed for admin only.",
    icon: ShieldAlert,
    accent: "border-blue-300 bg-blue-50 dark:bg-blue-950/30 dark:border-blue-800/50",
    badge: "bg-blue-600 text-white",
    button: "bg-blue-600 hover:bg-blue-700",
    testid: "founder-real",
  },
  {
    kind: "demo",
    title: "DEMO ACCESS",
    subtitle: "Seeded TEST/demo records. Founder data is tagged & resettable. Cannot leak into customer metrics.",
    icon: Crown,
    accent: "border-amber-300 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/50",
    badge: "bg-amber-600 text-white",
    button: "bg-amber-600 hover:bg-amber-700",
    testid: "founder-demo",
  },
];

export default function AdminFounderMode() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState("");
  const [log, setLog] = useState([]);
  const [logLoading, setLogLoading] = useState(true);

  const loadLog = useCallback(async () => {
    setLogLoading(true);
    try {
      const r = await axios.get(`${API}/api/admin/founder-mode/log`, { withCredentials: true });
      setLog(r.data.events || []);
    } catch (e) { /* admin-only; silent */ }
    setLogLoading(false);
  }, []);

  useEffect(() => { loadLog(); }, [loadLog]);

  const start = async (kind, role) => {
    setBusy(`start:${kind}:${role}`);
    try {
      // Wipe stale state BEFORE switching modes (closes Quick Debrief
      // recorders, clears localStorage role keys, etc.)
      clearRoleScopedStorage();
      const r = await axios.post(
        `${API}/api/admin/founder-mode/start`,
        { mode: kind, role },
        { withCredentials: true }
      );
      await refreshUser();
      toast.success(`${kind === "real" ? "REAL" : "DEMO"} mode started as ${role.toUpperCase()}`);
      navigate(r.data.redirect_to || "/", { replace: true });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Start failed");
    }
    setBusy("");
  };

  const reset = async (kind, role) => {
    const label = kind === "real"
      ? `Reset onboarding flags for REAL ${role.toUpperCase()}? (Customer data is NEVER touched.)`
      : `Reset ALL DEMO ${role.toUpperCase()} data? Founder-tagged rows will be wiped.`;
    if (!window.confirm(label)) return;
    setBusy(`reset:${kind}:${role}`);
    try {
      const r = await axios.post(
        `${API}/api/admin/founder-mode/reset`,
        { mode: kind, role },
        { withCredentials: true }
      );
      const counts = r.data.deleted_counts || {};
      const total = Object.values(counts).reduce((a, b) => a + b, 0);
      toast.success(`Reset complete · ${total} row${total === 1 ? "" : "s"} cleared`);
      await loadLog();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Reset failed");
    }
    setBusy("");
  };

  return (
    <div className="min-h-dvh no-overflow-x bg-slate-50 dark:bg-[#0c1120] text-slate-900 dark:text-slate-100">
      <header className="bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-40">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link
            data-testid="founder-mode-back"
            to="/admin"
            className="p-2 -ml-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div className="flex-1">
            <h1 className="font-bold text-lg leading-tight flex items-center gap-2">
              <Crown className="w-4 h-4 text-amber-500" /> Founder Operating Mode
            </h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Switch between REAL (production) and DEMO (seeded) experiences for any role.
            </p>
          </div>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5">
        {user?.operating_mode && (
          <div data-testid="founder-mode-active-notice" className="rounded-2xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800/50 p-4">
            <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-200">
              Currently active: <span className="uppercase">{user.operating_mode.replace("_", " · ")}</span>
            </p>
            <p className="text-xs text-emerald-800/80 dark:text-emerald-200/70 mt-1">
              Use the banner at the top of any page to return to admin.
            </p>
          </div>
        )}

        {/* Safety guarantees */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-xs uppercase tracking-wide font-bold text-emerald-700 dark:text-emerald-400 flex items-center gap-1.5 mb-2">
            <ShieldCheck className="w-3.5 h-3.5" /> Safety guarantees
          </p>
          <ul className="text-sm text-slate-700 dark:text-slate-300 space-y-1.5 list-disc pl-5">
            <li>Stripe is bypassed only for admin — no real charges, no fake subscriptions, no silent redirects.</li>
            <li>REAL mode hides any record matching TEST_/demo/seed/fake patterns.</li>
            <li>DEMO mode tags every founder row & resets cleanly without touching customer data.</li>
            <li>Mode switches clear stale state (recorders, localStorage role keys, lesson timers).</li>
          </ul>
        </div>

        {/* Two distinct sections — REAL and DEMO */}
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.kind}
              data-testid={m.testid}
              className={`rounded-2xl border-2 p-4 ${m.accent}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className="w-4 h-4" />
                <p className="font-extrabold text-sm tracking-wide">{m.title}</p>
                <span className={`ml-auto text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded-full ${m.badge}`}>
                  {m.kind}
                </span>
              </div>
              <p className="text-xs text-slate-600 dark:text-slate-400 mb-3">{m.subtitle}</p>
              <div className="space-y-2">
                {ROLES.map((r) => (
                  <div
                    key={r.id}
                    data-testid={`${m.testid}-role-${r.id}`}
                    className="flex items-center gap-3 bg-white dark:bg-slate-900 rounded-xl p-3 border border-slate-200 dark:border-slate-700"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-sm">
                        {m.kind === "real" ? "Become Real" : "Enter Demo"} {r.label}
                      </p>
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{r.helper}</p>
                    </div>
                    <Button
                      data-testid={`${m.testid}-start-${r.id}`}
                      onClick={() => start(m.kind, r.id)}
                      disabled={!!busy}
                      size="sm"
                      className={`${m.button} text-white font-bold`}
                    >
                      <Play className="w-3.5 h-3.5 mr-1.5" />
                      {busy === `start:${m.kind}:${r.id}` ? "…" : "Enter"}
                    </Button>
                    <Button
                      data-testid={`${m.testid}-reset-${r.id}`}
                      onClick={() => reset(m.kind, r.id)}
                      disabled={!!busy}
                      size="sm"
                      variant="outline"
                      className="text-xs"
                    >
                      <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          );
        })}

        {/* Audit log */}
        <div className="rounded-2xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-4">
          <p className="text-xs uppercase tracking-wide font-bold text-slate-500 dark:text-slate-400 flex items-center gap-1.5 mb-3">
            <ScrollText className="w-3.5 h-3.5" /> Audit log
          </p>
          {logLoading ? (
            <p className="text-sm text-slate-400">Loading…</p>
          ) : log.length === 0 ? (
            <p className="text-sm text-slate-500" data-testid="founder-mode-log-empty">No events yet.</p>
          ) : (
            <ul data-testid="founder-mode-log" className="space-y-2 max-h-60 overflow-y-auto">
              {log.map((e, i) => (
                <li
                  key={i}
                  data-testid={`founder-mode-log-row-${i}`}
                  className="text-xs text-slate-600 dark:text-slate-400 border-b border-slate-100 dark:border-slate-800 pb-1.5 last:border-0"
                >
                  <span className="font-bold uppercase tracking-wide text-slate-800 dark:text-slate-200">{e.action}</span>
                  {e.mode && <span className="ml-2 text-blue-700 dark:text-blue-400">{e.mode}</span>}
                  {e.role && <span className="ml-1 text-amber-700 dark:text-amber-400">· {e.role}</span>}
                  <span className="ml-2 text-slate-400">{new Date(e.at).toLocaleString()}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
